import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "../../../shared/hooks/useHistory";

import { differenceInHours, differenceInMinutes } from "date-fns";
import { withStyles, createStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import PropTypes from "prop-types";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import Avatar from "@material-ui/core/Avatar";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import Typography from "@material-ui/core/Typography";
import clsx from "clsx";

import { useStyle } from "./notification.styles";
import {
  asyncGetNotifications,
  asyncNotificationRead,
  setEncourageMsgRead,
} from "./actions/notificationsActions";
import { NotificationIcon } from "../../icons";
import { formatUtcToTimezone } from "../../formattedDate";
import { PARTICIPANT_HOME_PATH } from "../../consts/participantHomePath";

const styles = createStyles((theme) => ({
  paper: {
    position: "relative",
    border: "1px solid #d3d4d5",
    width: "100%",
    maxWidth: "50ch",
    paddingLeft: "10px",
    paddingRight: "5px",
    display: "flex",
    flexWrap: "wrap",
    top: "60px !important",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
      maxWidth: "100%",
      padding: 0,
      left: "0 !important",
      height: "100%",
      maxHeight: "calc(100% - 72px)",
      top: "72px !important",
    },
  },
  list: {
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      paddingTop: 0,
      paddingRight: "0 !important",
    },
  },
}));

const StyledMenu = withStyles(styles)((props) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "center",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "center",
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    width: "100%",
    minHeight: "60px",
    borderBottomStyle: "inset",
    borderBottom: "rgba(0, 0, 0, 0.04)",
    whiteSpace: "unset",
    [theme.breakpoints.down("xs")]: {
      padding: "12px 32px",
    },
    "&:hover": {
      backgroundColor: "#F1F7FF",
    },
  },
}))(MenuItem);

const profilInformationToFill = {
  annualIncome: {
    title:
      "Complete your profile so we can customize your content and recommendations!",
    description: "Add your annual income in your profile.",
  },
  dateOfBirth: {
    title:
      "Complete your profile so we can customize your content and recommendations!",
    description: "Add your birth date in your profile.",
  },
  firstFinancialGoal: {
    title: "Add your #1 Financial Goal!",
    description: "Go to my profile now.",
  },
  maritalStatusId: {
    title:
      "Complete your profile so we can customize your content and recommendations!",
    description: "Add your marital status in your profile.",
  },
};

const SentAtNotification = ({ sentAt }) => {
  const parseDate = formatUtcToTimezone(sentAt);
  const diffHours = differenceInHours(new Date(), parseDate);
  const diffMinutes = differenceInMinutes(new Date(), parseDate);
  return (
    <span
      style={{
        fontSize: 10,
      }}
    >
      {diffHours > 24
        ? parseDate.toLocaleDateString()
        : diffMinutes < 60
        ? `${diffMinutes} min(s) ago.`
        : `${diffHours} hour(s) ago.`}
    </span>
  );
};

SentAtNotification.propTypes = {
  sentAt: PropTypes.string.isRequired,
};

SentAtNotification.defaultProps = {};

export function Notifications({ anchorEl, onClose, handleSetNotRead }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyle();
  const [profileNotifications, setProfileNotifications] = useState([]);
  const { notifications, participantProfile, participantProfileId } =
    useSelector((state) => {
      return {
        notifications: state.notificationsReducer.notifications,
        participantProfile: state.profile,
        participantProfileId: state.auth.login.participantType,
      };
    });

  useEffect(() => {
    const arr = [];
    if (!participantProfile.dateOfBirth) {
      arr.push(profilInformationToFill.dateOfBirth);
    }
    if (participantProfile.maritalStatusId === 0) {
      arr.push(profilInformationToFill.maritalStatusId);
    }
    if (
      participantProfile.annualIncome < 0 ||
      participantProfile.annualIncome === null
    ) {
      arr.push(profilInformationToFill.annualIncome);
    }
    setProfileNotifications(arr);
  }, [participantProfile]);

  useEffect(() => {
    dispatch(asyncGetNotifications());
  }, [dispatch]);

  useEffect(() => {
    if (profileNotifications.length > 0 || notifications?.length > 0)
      handleSetNotRead(true);
    else handleSetNotRead(false);
  }, [notifications, profileNotifications, handleSetNotRead]);

  const handleOnCLickNotification = (notification) => {
    const path =
      notification.url.charAt() === "/"
        ? notification.url
        : `/${notification.url}`;
    if (notification.url) {
      history.push(`${PARTICIPANT_HOME_PATH}${path}`);
    }
    dispatch(setEncourageMsgRead(notification));
    dispatch(asyncNotificationRead(notification.internalMessageId));
    onClose();
  };

  const handleOnClickProfileNotification = (notification) => {
    dispatch(setEncourageMsgRead(notification));
    history.push(`${PARTICIPANT_HOME_PATH}/participantProfile`);
    onClose();
  };

  const currDate = new Date().toLocaleDateString();

  return (
    <StyledMenu
      id="customized-menu"
      data-testid="notifications"
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={() => onClose()}
    >
      {notifications?.length > 0 || profileNotifications.length > 0 ? (
        <div>
          {notifications?.map((notification) => (
            <StyledMenuItem
              key={notification.internalMessageId}
              alignItems="flex-start"
              onClick={(e) => {
                e.preventDefault();
                handleOnCLickNotification(notification);
              }}
            >
              {participantProfileId !== 1 ? (
                <ListItemIcon>
                  {notification.url ? (
                    <Avatar>
                      <AssignmentOutlinedIcon />
                    </Avatar>
                  ) : (
                    <Avatar alt="userPic" src={notification.profilePicture} />
                  )}
                </ListItemIcon>
              ) : null}

              <ListItemText
                primary={
                  <Typography
                    className={
                      participantProfileId !== 1 ? null : classes.mainText
                    }
                    style={{ fontWeight: "bold" }}
                  >
                    {notification.subject}
                  </Typography>
                }
                secondary={
                  <span className={classes.listItem}>
                    <Typography
                      component="span"
                      variant="body2"
                      color="textPrimary"
                      className={clsx(classes.inline, {
                        [classes.generalText]: participantProfileId === 1,
                      })}
                    >
                      {notification.body}
                    </Typography>
                    <SentAtNotification sentAt={notification.sentAt} />
                  </span>
                }
              />
            </StyledMenuItem>
          ))}
          {profileNotifications?.map((notification) => (
            <StyledMenuItem
              key={notification.description}
              id={notification}
              alignItems="flex-start"
              onClick={(e) => {
                e.preventDefault();
                handleOnClickProfileNotification(notification);
              }}
            >
              {participantProfileId !== 1 ? (
                <ListItemIcon>
                  <AccountCircleIcon style={{ fontSize: "2.8rem" }} />
                </ListItemIcon>
              ) : null}

              <ListItemText
                primary={
                  <Typography
                    style={{ fontWeight: "bold" }}
                    className={
                      participantProfileId !== 1 ? null : classes.mainText
                    }
                  >
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <span className={classes.listItem}>
                    <Typography
                      component="span"
                      variant="body2"
                      color="textPrimary"
                      className={
                        participantProfileId !== 1 ? null : classes.generalText
                      }
                    >
                      {notification.description}
                    </Typography>
                    <span
                      style={{
                        fontSize: "10px",
                      }}
                      className={
                        participantProfileId !== 1 ? null : classes.dateText
                      }
                    >
                      {currDate}
                    </span>
                  </span>
                }
              />
            </StyledMenuItem>
          ))}
        </div>
      ) : participantProfileId === 1 ? (
        <Box className={classes.containerNoNotification}>
          <Box className={classes.noNotification}>
            <NotificationIcon />
            <h3>No new alerts</h3>
            <p className={classes.secundaryText}>
              You currently have no new notifications.
            </p>
          </Box>
        </Box>
      ) : (
        <StyledMenuItem alignItems="flex-start">
          <ListItemText primary="You currently have no new notifications" />
        </StyledMenuItem>
      )}
    </StyledMenu>
  );
}
Notifications.propTypes = {
  anchorEl: PropTypes.instanceOf(Object),
  onClose: PropTypes.func.isRequired,
  handleSetNotRead: PropTypes.func.isRequired,
};
Notifications.defaultProps = {
  anchorEl: null,
};
